import { Point, Markup } from '@/types';
import { getMarkupVertices } from '@/core/markup-geometry/legacy-compat';

export { getMarkupSegments } from '@/core/markup-geometry/legacy-compat';

/**
 * Functional core module for pure geometry calculations.
 * All functions are pure (no side effects) and work with page coordinates.
 * 
 * COORDINATE SPACES:
 * 
 * 1. Screen Space (pixels)
 *    - Browser viewport coordinates from mouse events (e.clientX/Y)
 *    - Origin at top-left of browser window
 *    - Used for: mouse events, DOM measurements
 * 
 * 2. Page Space (PDF units)
 *    - Coordinates in PDF page units (typically points, 1/72 inch)
 *    - Origin at top-left of PDF page
 *    - Used for: markup storage, geometry calculations, rendering
 * 
 * 3. Normalized Space [0,1]
 *    - Coordinates normalized to [0,1] range relative to page dimensions
 *    - Origin at top-left (0,0), bottom-right is (1,1)
 *    - Used for: realtime sync, resolution-independent calculations
 * 
 * CONVERSION GUIDELINES:
 * - Realtime sync → Always normalize before sending, denormalize before rendering
 * - Zoom/pan calculations → Use normalized space to avoid rounding errors
 * - Markup storage → Keep in page space for Firestore (persistent data)
 */

/**
 * Calculate Euclidean distance between two points
 */
export function distanceBetweenPoints(p1: Point, p2: Point): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Snap-to-vertex threshold in screen pixels
 * Used when drawing polylines/polygons to detect when cursor is near first vertex
 */
export const SNAP_THRESHOLD_PIXELS = 15;

/**
 * Check if a point is near another point within a screen-pixel threshold
 * @param point Current point in page space
 * @param target Target point in page space
 * @param thresholdPixels Snap distance in screen pixels (e.g., 15)
 * @param zoom Current viewport zoom level
 * @returns true if point is within snap threshold of target
 */
export function isPointNearTarget(
    point: Point,
    target: Point,
    thresholdPixels: number,
    zoom: number
): boolean {
    // Calculate distance in page space
    const pageDistance = distanceBetweenPoints(point, target);
    // Convert threshold from screen pixels to page space
    const pageThreshold = thresholdPixels / zoom;
    return pageDistance <= pageThreshold;
}

/**
 * Constrain a point to the nearest axis (horizontal or vertical) relative to an anchor point
 * Used for drawing tools to snap endpoints to orthogonal directions
 * @param anchor Reference point (typically the last committed point in a drawing)
 * @param cursor Current cursor position to be constrained
 * @returns Point constrained to horizontal axis (if dx >= dy) or vertical axis (if dx < dy)
 */
export function constrainToAxis(anchor: Point, cursor: Point): Point {
    const dx = Math.abs(cursor.x - anchor.x);
    const dy = Math.abs(cursor.y - anchor.y);
    
    // When dx equals dy, default to horizontal constraint
    if (dx >= dy) {
        // Horizontal constraint: lock Y coordinate to anchor
        return { x: cursor.x, y: anchor.y };
    } else {
        // Vertical constraint: lock X coordinate to anchor
        return { x: anchor.x, y: cursor.y };
    }
}

/**
 * Collect all vertices from visible markups, excluding specified markup IDs.
 * @param markups All markups on the page
 * @param excludeIds IDs of markups to exclude (e.g., currently editing markup)
 * @returns Array of all vertices from non-excluded visible markups
 */
export function collectAllVertices(
    markups: Markup[],
    excludeIds: string[] = []
): Point[] {
    // Build set of all IDs to exclude (including group children)
    const excludeSet = new Set(excludeIds);

    // Find all groups in exclude list and add their children
    for (const id of excludeIds) {
        const markup = markups.find(m => m.id === id);
        if (markup?.type === 'group') {
            const groupMarkup = markup as { childIds?: string[] };
            const childIds = groupMarkup.childIds || [];
            childIds.forEach(childId => excludeSet.add(childId));
        }
    }

    const vertices: Point[] = [];

    for (const markup of markups) {
        // Skip if markup is in exclude list
        if (excludeSet.has(markup.id)) {
            continue;
        }

        // Skip if markup is invisible
        if (markup.visible === false) {
            continue;
        }

        // Skip groups themselves (we only want vertices from their children)
        if (markup.type === 'group') {
            continue;
        }

        // Add vertices from this markup
        vertices.push(...getMarkupVertices(markup, markups));
    }

    return vertices;
}

/**
 * Project a point onto a line defined by two points.
 * Returns the projection point, distance from point to line, and parameter t.
 * @param point Point to project
 * @param lineStart Start of line segment
 * @param lineEnd End of line segment
 * @returns Object with projection point, distance, and parameter t (0=start, 1=end)
 */
export function projectPointOntoLine(
    point: Point,
    lineStart: Point,
    lineEnd: Point
): { projection: Point; distance: number; t: number } {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const lengthSquared = dx * dx + dy * dy;

    // Line is actually a point
    if (lengthSquared === 0) {
        return {
            projection: lineStart,
            distance: distanceBetweenPoints(point, lineStart),
            t: 0
        };
    }

    // Calculate parameter t (position along line)
    // t = 0 means projection is at lineStart
    // t = 1 means projection is at lineEnd
    // t < 0 or t > 1 means projection is outside segment
    const t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared;

    const projection = {
        x: lineStart.x + t * dx,
        y: lineStart.y + t * dy
    };

    return {
        projection,
        distance: distanceBetweenPoints(point, projection),
        t
    };
}

/**
 * Check if a point is horizontally aligned with a target Y coordinate.
 * @param point Point to check
 * @param targetY Target Y coordinate
 * @param threshold Alignment threshold in page units
 * @returns Object with aligned flag and snap Y coordinate
 */
export function detectHorizontalAlignment(
    point: Point,
    targetY: number,
    threshold: number
): { aligned: boolean; snapY: number } {
    const distance = Math.abs(point.y - targetY);
    return {
        aligned: distance <= threshold,
        snapY: targetY
    };
}

/**
 * Check if a point is vertically aligned with a target X coordinate.
 * @param point Point to check
 * @param targetX Target X coordinate
 * @param threshold Alignment threshold in page units
 * @returns Object with aligned flag and snap X coordinate
 */
export function detectVerticalAlignment(
    point: Point,
    targetX: number,
    threshold: number
): { aligned: boolean; snapX: number } {
    const distance = Math.abs(point.x - targetX);
    return {
        aligned: distance <= threshold,
        snapX: targetX
    };
}

/**
 * Check if a point is collinear with a line (lies on its extension).
 * Only considers points outside the segment (t < 0 or t > 1).
 * @param point Point to check
 * @param lineStart Start of line segment
 * @param lineEnd End of line segment
 * @param threshold Distance threshold in page units
 * @returns Object with aligned flag, snap point, and extension flag
 */
export function detectCollinearAlignment(
    point: Point,
    lineStart: Point,
    lineEnd: Point,
    threshold: number
): { aligned: boolean; snapPoint: Point | null; isExtension: boolean } {
    const { projection, distance, t } = projectPointOntoLine(point, lineStart, lineEnd);

    // Only consider as collinear if point is outside segment (extension)
    // Use small buffer to avoid edge cases at segment endpoints
    const isExtension = t < -0.1 || t > 1.1;

    if (isExtension && distance <= threshold) {
        return {
            aligned: true,
            snapPoint: projection,
            isExtension: true
        };
    }

    return {
        aligned: false,
        snapPoint: null,
        isExtension: false
    };
}
